import React from 'react';
import { useTranslation } from '@tasama/i18n';
import { TermsAndConditionsContent } from '@tasama/ui';
import type { TermsAndConditionsContentProps } from '@tasama/ui';

interface TermsAndConditionsModalProps {
  className?: string;
  testID?: string;
}

/**
 * TermsAndConditionsModal
 * Wrapper component that handles i18n translations and passes them to the pure UI component.
 * This component bridges the gap between the business logic (translations) and the UI component.
 */
export const TermsAndConditionsModal: React.FC<
  TermsAndConditionsModalProps
> = ({ className, testID = 'terms-conditions-modal' }) => {
  const { t } = useTranslation('onboarding');

  // Prepare the translated content for the UI component
  const termsContent: TermsAndConditionsContentProps = {
    className,
    testID,
    sections: {
      acceptanceOfTerms: {
        title: t('termsAndConditions.sections.acceptanceOfTerms.title'),
        content: t('termsAndConditions.sections.acceptanceOfTerms.content'),
      },
      websiteUse: {
        title: t('termsAndConditions.sections.websiteUse.title'),
        content: t('termsAndConditions.sections.websiteUse.content'),
      },
      userRegistration: {
        title: t('termsAndConditions.sections.userRegistration.title'),
        content: t('termsAndConditions.sections.userRegistration.content'),
        items: t('termsAndConditions.sections.userRegistration.items', {
          returnObjects: true,
        }) as string[],
        additionalContent: t(
          'termsAndConditions.sections.userRegistration.additionalContent'
        ),
      },
      confidentiality: {
        title: t('termsAndConditions.sections.confidentiality.title'),
        content: t('termsAndConditions.sections.confidentiality.content'),
      },
      cookiesAndWebsiteData: {
        title: t('termsAndConditions.sections.cookiesAndWebsiteData.title'),
        content: t('termsAndConditions.sections.cookiesAndWebsiteData.content'),
        items: t('termsAndConditions.sections.cookiesAndWebsiteData.items', {
          returnObjects: true,
        }) as string[],
        additionalContent: t(
          'termsAndConditions.sections.cookiesAndWebsiteData.additionalContent'
        ),
      },
      intellectualProperty: {
        title: t('termsAndConditions.sections.intellectualProperty.title'),
        content: t('termsAndConditions.sections.intellectualProperty.content'),
      },
      limitationOfLiability: {
        title: t('termsAndConditions.sections.limitationOfLiability.title'),
        content: t('termsAndConditions.sections.limitationOfLiability.content'),
      },
      thirdPartyLinks: {
        title: t('termsAndConditions.sections.thirdPartyLinks.title'),
        content: t('termsAndConditions.sections.thirdPartyLinks.content'),
      },
      governingLaw: {
        title: t('termsAndConditions.sections.governingLaw.title'),
        content: t('termsAndConditions.sections.governingLaw.content'),
      },
      amendments: {
        title: t('termsAndConditions.sections.amendments.title'),
        content: t('termsAndConditions.sections.amendments.content'),
      },
    },
  };

  return <TermsAndConditionsContent {...termsContent} />;
};

TermsAndConditionsModal.displayName = 'TermsAndConditionsModal';
export default TermsAndConditionsModal;
export type { TermsAndConditionsModalProps };
